import React, { useCallback, useMemo, useState } from 'react';
import { FiDownload, FiExternalLink } from 'react-icons/fi';
import { getOfficeDocumentKind, officeDocumentKindLabel } from './officeDocumentPreview';

/**
 * Office files are opened via download / system app — no third-party parsers in the browser.
 */
export default function MobileMediaViewerOffice({
  src,
  fileName = '',
  officeKind,
  className = '',
  fetchCredentials = 'include',
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const resolvedKind = useMemo(
    () => officeKind || getOfficeDocumentKind('', fileName),
    [officeKind, fileName]
  );

  const kindLabel = officeDocumentKindLabel(resolvedKind);
  const safeName = fileName || 'Document';

  const downloadFile = useCallback(async () => {
    if (!src || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(src, { credentials: fetchCredentials });
      if (!res.ok) {
        throw new Error(res.status === 401 ? 'Sign in required' : 'Download failed');
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = safeName;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError(e.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  }, [src, busy, fetchCredentials, safeName]);

  const openInNewTab = useCallback(() => {
    if (!src) return;
    window.open(src, '_blank', 'noopener,noreferrer');
  }, [src]);

  const rootClass = ['mobile-media-viewer-overlay__office-wrap', className].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      <div className="mobile-media-viewer-office mobile-media-viewer-office--external">
        <p className="mobile-media-viewer-office__kind">{kindLabel}</p>
        <h2 className="mobile-media-viewer-office__name" title={safeName}>{safeName}</h2>
        <p className="mobile-media-viewer-office__hint">
          Word, Excel, and PowerPoint files open best in Microsoft Office, Google Docs, or another app on
          your device. Use download or open in a new tab below.
        </p>
        {error ? (
          <p className="mobile-media-viewer-office__error" role="alert">{error}</p>
        ) : null}
        <div className="mobile-media-viewer-office__actions">
          <button type="button" className="mobile-media-viewer-office__btn" onClick={downloadFile} disabled={busy}>
            <FiDownload aria-hidden />
            {busy ? 'Downloading…' : 'Download'}
          </button>
          <button type="button" className="mobile-media-viewer-office__btn mobile-media-viewer-office__btn--secondary" onClick={openInNewTab}>
            <FiExternalLink aria-hidden />
            Open in new tab
          </button>
        </div>
      </div>
    </div>
  );
}
